// Story 7.18 (ADO-1387) — the camera scan modal (AC2/AC6/AC10/AC12), redesigned to
// the official designer screens (SCAN-02..06, Figma 3304-9976).
//
// The thin outer shell: it owns the camera stream + the decode loop, draws the
// full-bleed scanner chrome (cart chip, close, the reticle + red scan line over the
// live feed) and drives the (camera-free, unit-tested) `ScanResultPanel` with a
// `ScanPhase`. Built on the design-system `Modal` `fullscreen`/`hideChrome` variant,
// which still provides the focus trap, Escape/close, focus restore and
// body-scroll-lock (AC12).
//
// AC6 (redesign D-7): on a single match the decode loop is already stopped (the
// first accepted decode stops it), the feed is DIMMED, and the product is shown in
// an in-overlay bottom-sheet — the viewport stays VISIBLE behind it (AC6 preserved),
// just no longer actively decoding. Add (via the Story 7.9 path) shows the design-system
// "Added to cart" toast (`Qty {n} · {product}`, Figma 4002:14600) and RESUMES scanning on
// the still-live stream, so the scanner stays open for the next product (SCAN-05/06). The
// toast is drawn and announced INSIDE this dialog rather than by the app-wide ToastHost:
// some screen readers ignore a live region outside an aria-modal dialog. The confirm sheet
// is an in-overlay panel, never a nested modal, so there is only ever one `aria-modal`.
//
// AC2 privacy: only the decoded digit string ever leaves the browser (via the
// resolution route). No frame is captured to a canvas we keep, none is uploaded, and
// no image-recognition service is called — the decoder reads the frame in memory.
//
// The camera plumbing (getUserMedia, <video>.play, the detect loop) is verified in a
// real browser (Playwright / manual), not happy-dom — the repo's established posture
// for camera/layout behaviour. The decode→resolve→phase branching it drives is unit
// tested via `scanResolution.ts` + `ScanResultPanel`.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Icon, Modal, Toast } from '../../design-system';
import type { SearchResultProduct } from '../../api/search.client';
import { useBasketStore } from '../../basket/useBasketStore';
import { UNKNOWN_TOTAL } from '../../basket/cartTotals';
import { useCartValue } from '../../basket/useCartValue';
import { formatMoney } from '../../shared/money';
import { resolveCatalogueEntryKey } from '../../ordering/catalogueEntry';
import { createBarcodeDetectorDecoder, type BarcodeDecoderPort } from './barcodeDecoder';
import {
  cameraErrorReason,
  phaseForProduct,
  resolveScan,
  SCAN_STATUS_COPY,
  type CameraErrorReason,
  type ScanPhase,
} from './scanResolution';
import { ScanResultPanel, type ScanAdded } from './ScanResultPanel';
import styles from './ScanModal.module.css';

/** How often (ms) the decode loop samples the live frame while scanning. */
const SCAN_INTERVAL_MS = 250;

/** How long (ms) the "Added to cart" toast shows before clearing (SCAN-05). */
const TOAST_WINDOW_MS = 2500;

/** After an add, ignore the SAME barcode (still in front of the lens) for this long
 *  so resuming doesn't instantly re-open the confirm sheet for the item just added. */
const LAST_ADDED_COOLDOWN_MS = 3000;

/** Tolerate this many CONSECUTIVE decode rejections before failing the camera. A
 *  single transient `detect()` hiccup on the zxing-wasm decode path (which the
 *  ponyfill runs on every browser) must not tear down an otherwise-fine live stream; a run of them is a real
 *  fault. Any successful sample (even an empty one) resets the count. Only counted
 *  AFTER the first successful sample, so a warming-up wasm decoder whose init surfaces
 *  as `detect()` REJECTIONS isn't killed before it can decode (the stall watchdog
 *  below is the backstop if it never warms up). */
const MAX_CONSECUTIVE_DECODE_FAILURES = 3;

/** Fail the camera if no `detect()` sample lands within this long while actively
 *  scanning — the recovery backstop for the failure modes that DON'T reject: frames
 *  never arriving (`readyState` stuck < 2), a `detect()` that hangs and never settles,
 *  or a wasm decoder that never warms up. Generous enough that a normal sub-second
 *  decode never trips it; without it the reticle spins forever with no way out but
 *  Close, defeating AC7 recovery on the AC10 handheld target. */
const STALL_TIMEOUT_MS = 8000;
const STALL_TICKS = Math.ceil(STALL_TIMEOUT_MS / SCAN_INTERVAL_MS);

export interface ScanModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Fall back to text search (AC7 / AC12). Invoked with NO argument, then the modal
   *  closes — the scanned barcode never reaches the caller's search box (AC8). */
  readonly onUseTextSearch?: () => void;
  /** Decode port (DD-3). Injectable so tests never load the wasm decoder; defaults to
   *  the `barcode-detector` ponyfill adapter. */
  readonly decoder?: BarcodeDecoderPort;
}

function stopStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => track.stop());
}

export function ScanModal({ open, onClose, onUseTextSearch, decoder }: ScanModalProps) {
  const [phase, setPhase] = useState<ScanPhase>({ status: 'scanning' });
  // The "Added to cart" toast (SCAN-05): a transient confirmation of the LATEST add, shown
  // over the feed. A new add replaces it (a fresh `id`) and restarts its timer.
  const [addedToast, setAddedToast] = useState<(ScanAdded & { readonly id: number }) | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastIdRef = useRef(0);
  // Latest `open`, read from async add callbacks that may resolve after a close so a
  // post-close resume can't re-acquire the camera behind a shut modal.
  const openRef = useRef(open);
  // The just-added barcode + its cooldown timer — skipped by the decode loop so a
  // resume doesn't instantly re-detect the product still in frame. This holds the RAW
  // decoded string (what the loop compares against), NOT the server's normalised
  // GTIN-13: `normaliseGtin` left-zero-pads EAN-8/UPC-A to 13, so keying the guard on
  // the resolved `barcode` would never match the raw decode for those two symbologies.
  const lastAddedRef = useRef<string | null>(null);
  const lastAddedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The RAW decoded string of the scan currently in the confirm/resolve pipeline —
  // captured the instant the loop accepts a decode, so a successful add can seed the
  // re-detect guard with the exact value the loop will see again while the product is
  // still in frame (independent of any server-side normalisation).
  const lastDecodedRef = useRef<string | null>(null);
  // Live basket item count and cart value for the cart chip. The value is the lines
  // summed at the prices the server locked on them, the same
  // `useCartValue` the header cart button shows, or UNKNOWN_TOTAL while it is not known.
  const itemCount = useBasketStore((s) =>
    s.lines.reduce((total, line) => total + line.quantity, 0),
  );
  const cartValue = useCartValue();
  const cartTotal = cartValue === null ? UNKNOWN_TOTAL : formatMoney(cartValue);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const decoderRef = useRef<BarcodeDecoderPort | null>(null);
  const loopRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Gate the async loop callback so an in-flight `detect` that resolves after we have
  // already accepted a code (or closed) does not process another frame.
  const activeRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  // Skip a tick while a previous decode is still in flight — on slow (wasm) decoders
  // the 250ms interval would otherwise stack overlapping detect() calls.
  const detectingRef = useRef(false);
  // Count of CONSECUTIVE decode rejections; a single transient failure must not tear
  // down the live stream (see MAX_CONSECUTIVE_DECODE_FAILURES).
  const decodeFailuresRef = useRef(0);
  // True once at least one `detect()` has RESOLVED — until then, rejections are decoder
  // warmup (wasm init on the ponyfill path), not a fault, so they don't count toward
  // the failure run. The stall watchdog still fires if warmup never completes.
  const hasSampledRef = useRef(false);
  // Ticks since the last `detect()` sample landed. Reset only on a resolved detect
  // (a rejection does NOT reset it — a decoder that only ever rejects is still stalled);
  // trips the watchdog at STALL_TICKS.
  const stalledTicksRef = useRef(0);
  // True while the loop is paused by a tab/app backgrounding — so the visibilitychange
  // handler only RESUMES a scan it itself paused, never clobbers a confirm/message
  // sheet the user backgrounded on.
  const wasScanningRef = useRef(false);
  // Set when more than one barcode is in frame (AC5 is "one code → many products", not
  // "many codes"): the loop can't know which the user means, so it nudges rather than
  // silently resolving an arbitrary `values[0]`.
  const [multiCodeHint, setMultiCodeHint] = useState(false);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const stopLoop = useCallback(() => {
    if (loopRef.current !== null) {
      clearInterval(loopRef.current);
      loopRef.current = null;
    }
  }, []);

  // Fail to a retryable camera error (AC7). Crucially it DROPS the stream so a
  // subsequent "Try again" re-acquires from scratch and re-attaches to the
  // freshly-mounted <video> — reusing a stream after the viewport unmounted (the
  // cameraError state hides it) would leave a black viewport that never decodes.
  const failCamera = useCallback(
    (reason: CameraErrorReason) => {
      activeRef.current = false;
      stopLoop();
      stopStream(streamRef.current);
      streamRef.current = null;
      setPhase({ status: 'cameraError', reason });
    },
    [stopLoop],
  );

  // The live track ended out from under us mid-scan — OS revoked permission, another
  // app grabbed the camera, or the device was unplugged. There is no `detect()`
  // rejection for this (the loop just goes quiet), so listen for it explicitly and
  // fail to the retryable camera error (AC7). Guarded on `openRef` so a stray fire
  // during teardown of a closed modal is a no-op. Manual `track.stop()` does not fire
  // `ended`, so our own teardown never re-triggers this.
  const handleTrackEnded = useCallback(() => {
    if (openRef.current) failCamera('unavailable');
  }, [failCamera]);

  const resolveDecoded = useCallback(async (code: string) => {
    setPhase({ status: 'resolving', barcode: code });
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      // The entry selected when the barcode was read (Enabler E7-C, ADO-2274, D-1).
      setPhase(await resolveScan(resolveCatalogueEntryKey(), code, ac.signal));
    } catch {
      // Only an AbortError re-throws out of resolveScan (a superseded/closed scan) —
      // ignore it; every other failure already returned a `resolveError` phase.
    }
  }, []);

  const startLoop = useCallback(() => {
    stopLoop();
    detectingRef.current = false;
    decodeFailuresRef.current = 0;
    hasSampledRef.current = false;
    stalledTicksRef.current = 0;
    loopRef.current = setInterval(() => {
      if (!activeRef.current) return;
      // Stall watchdog: no sample has landed for STALL_TICKS. Covers the failure modes
      // that never REJECT (so decodeFailures never advances) — frames never arriving
      // (readyState stuck < 2, so detect() is never called), a hung detect() (detecting
      // stuck true), or a wasm decoder that never warms up. Runs before the detecting
      // short-circuit so a hung detect is caught.
      stalledTicksRef.current += 1;
      if (stalledTicksRef.current >= STALL_TICKS) {
        failCamera('unavailable');
        return;
      }
      if (detectingRef.current) return;
      const video = videoRef.current;
      const detector = decoderRef.current;
      // readyState < HAVE_CURRENT_DATA → no frame to decode yet.
      if (!video || !detector || video.readyState < 2) return;
      detectingRef.current = true;
      void detector
        .detect(video)
        .then((values) => {
          hasSampledRef.current = true; // the decoder works — warmup is over
          decodeFailuresRef.current = 0; // a sample landed — clear the failure run
          stalledTicksRef.current = 0; // …and the stall counter (the loop is alive)
          if (!activeRef.current) return;
          // More than one barcode in frame (multi-pack, two products together): the
          // loop can't know which the user means, so nudge instead of resolving an
          // arbitrary values[0] and adding the wrong product silently (AC5 boundary).
          if (values.length > 1) {
            setMultiCodeHint(true);
            return;
          }
          setMultiCodeHint(false);
          // A decoder can emit '' / whitespace-only noise; that is NOT a barcode. Skip
          // it (keep scanning) rather than send it and get the generic transport error
          // instead of the distinct AC7 "not a valid barcode" message.
          const code = values[0]?.trim();
          if (!code) return;
          // Ignore the product just added and still in frame — keep scanning until a
          // different code (or the cooldown) so the confirm sheet doesn't re-open on
          // top of its own "Added to cart" toast.
          if (code === lastAddedRef.current) return;
          activeRef.current = false; // accept the first decode; stop sampling
          lastDecodedRef.current = code; // seed the re-detect guard with the RAW decode
          stopLoop();
          void resolveDecoded(code);
        })
        .catch(() => {
          if (!activeRef.current) return;
          // A decoder failure (zxing-wasm load/instantiate, or an InvalidStateError
          // on a bad frame) must not throw on every
          // tick and leave the scanner spinning silently on 'scanning'. But a lone
          // transient hiccup must not kill an otherwise-fine stream either — only a
          // RUN of consecutive failures AFTER the decoder has proven it works surfaces
          // the retryable camera error (AC7); pre-warmup rejections are the stall
          // watchdog's job, not this counter's. Deliberately does NOT reset the stall
          // counter — a decoder that only ever rejects is still stalled.
          if (!hasSampledRef.current) return;
          decodeFailuresRef.current += 1;
          if (decodeFailuresRef.current >= MAX_CONSECUTIVE_DECODE_FAILURES) {
            failCamera('unavailable');
          }
        })
        .finally(() => {
          detectingRef.current = false;
        });
    }, SCAN_INTERVAL_MS);
  }, [resolveDecoded, stopLoop, failCamera]);

  const acquireAndScan = useCallback(async () => {
    activeRef.current = true;
    setMultiCodeHint(false);
    setPhase({ status: 'scanning' });
    try {
      decoderRef.current ??= decoder ?? createBarcodeDetectorDecoder();
      if (!streamRef.current) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        // Close-race: if the modal closed (or a newer acquire superseded this one)
        // while the permission prompt / acquisition was pending, the effect cleanup
        // already ran and stopped nothing (streamRef was still null). Abandon this
        // just-acquired stream so the camera does not stay live behind a closed modal.
        //
        // Acquire-race: two overlapping acquires (fast retry / resume) can both pass
        // the null check and both await getUserMedia. Whichever assigns streamRef
        // first wins; the loser must drop its own stream rather than overwrite the ref
        // and leave the winner's tracks running orphaned. Either way the winner
        // attaches + starts the loop, so the loser just bows out here.
        if (!activeRef.current || streamRef.current) {
          stopStream(stream);
          return;
        }
        streamRef.current = stream;
        // Fail to a retryable error if this track ends unexpectedly (revoke / device
        // loss). Attached only on a FRESH acquire — a reused stream (resume) keeps the
        // listener from its first acquire, so it is never double-bound. `getVideoTracks`
        // / `addEventListener` are optional-chained so a mocked stream without them is a
        // no-op rather than a throw.
        const track = stream.getVideoTracks?.()?.[0] ?? stream.getTracks?.()?.[0];
        track?.addEventListener?.('ended', handleTrackEnded);
      }
      // Attach the (new or reused) stream to the CURRENT <video> every time —
      // decoupled from acquisition so a viewport remounted after a message/retry
      // still shows the feed (never a black viewport over a live stream).
      const video = videoRef.current;
      if (video) {
        if (video.srcObject !== streamRef.current) video.srcObject = streamRef.current;
        await video.play();
      }
      // Teardown (close/unmount) can run during the `await video.play()` above, which
      // sets activeRef false and clears loopRef. Without this re-check (mirroring the
      // acquire-race guard above) startLoop would then create a fresh interval nothing
      // is scheduled to clear until the next open/unmount — a dangling timer.
      if (!activeRef.current) return;
      startLoop();
    } catch (err) {
      // Acquire OR playback failure → a retryable camera error. `failCamera` drops the
      // stream so a retry re-acquires against the freshly-mounted viewport rather than
      // reusing a stream the (now-unmounted) video can no longer be attached to.
      failCamera(cameraErrorReason(err));
    }
  }, [decoder, startLoop, failCamera, handleTrackEnded]);

  // Open → acquire camera + scan; close/unmount → tear down the stream, loop and any
  // in-flight resolve so the camera light goes out and nothing updates a closed modal.
  useEffect(() => {
    if (!open) return;
    void acquireAndScan();
    // Backgrounding the tab/app leaves a dead stream (stuck) or one frozen on its last
    // frame (which would decode a barcode that was in view at suspend) on return. While
    // ACTIVELY scanning, release the camera on hide and re-acquire a fresh stream on
    // return. Gated on activeRef so it never touches a confirm/message/error the user
    // backgrounded on (those aren't scanning), and on wasScanningRef so it only resumes
    // a scan it itself paused.
    const onVisibility = () => {
      if (document.hidden) {
        if (!activeRef.current) return;
        activeRef.current = false;
        stopLoop();
        stopStream(streamRef.current);
        streamRef.current = null;
        const v = videoRef.current;
        if (v) v.srcObject = null;
        wasScanningRef.current = true;
      } else if (openRef.current && wasScanningRef.current) {
        wasScanningRef.current = false;
        void acquireAndScan();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      activeRef.current = false;
      wasScanningRef.current = false;
      stopLoop();
      stopStream(streamRef.current);
      streamRef.current = null;
      abortRef.current?.abort();
      if (toastTimer.current !== null) clearTimeout(toastTimer.current);
      if (lastAddedTimer.current !== null) clearTimeout(lastAddedTimer.current);
      lastAddedRef.current = null;
      lastDecodedRef.current = null;
      setMultiCodeHint(false);
      // Reset the phase so a stale confirm sheet / message card (with its role="alert"
      // + focus-on-mount) can't render — and be announced — for one frame on the next
      // open before the mount effect's acquireAndScan resets it to 'scanning'.
      setPhase({ status: 'scanning' });
      // Reset the toast so a close mid-window can't leave it stuck visible on the next
      // open: the cleanup cancels its timer, so nothing else would clear it.
      setAddedToast(null);
      const video = videoRef.current;
      if (video) video.srcObject = null;
    };
  }, [open, acquireAndScan, stopLoop]);

  const retry = useCallback(() => {
    void acquireAndScan();
  }, [acquireAndScan]);

  const selectCandidate = useCallback((product: SearchResultProduct) => {
    setPhase((prev) =>
      prev.status === 'disambiguate' ? phaseForProduct(prev.barcode, product) : prev,
    );
  }, []);

  // A successful add (SCAN-05): show the "Added to cart" toast for THIS add (replacing any
  // earlier one) and resume scanning on the still-live stream so the scanner stays open for
  // the next product (SCAN-06).
  // Guarded on `openRef` — the add loop can resolve AFTER the user closed the modal,
  // and resuming then would re-acquire the camera behind a shut scanner (the effect
  // cleanup already ran and won't run again).
  const handleAdded = useCallback(
    (added: ScanAdded) => {
      if (!openRef.current) return;
      // Skip re-detecting this product while it is still in frame. Keyed on the RAW
      // decode the loop actually produced (captured at accept time), NOT the resolved
      // barcode — the server normalises EAN-8/UPC-A to a zero-padded GTIN-13, which the
      // raw decode never equals, so a resolved-barcode key would never fire the guard
      // for those symbologies and the just-added item would re-open its own sheet.
      lastAddedRef.current = lastDecodedRef.current;
      if (lastAddedTimer.current !== null) clearTimeout(lastAddedTimer.current);
      lastAddedTimer.current = setTimeout(() => {
        lastAddedRef.current = null;
      }, LAST_ADDED_COOLDOWN_MS);
      toastIdRef.current += 1;
      setAddedToast({ ...added, id: toastIdRef.current });
      if (toastTimer.current !== null) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setAddedToast(null), TOAST_WINDOW_MS);
      void acquireAndScan();
    },
    [acquireAndScan],
  );

  const useTextSearch = useCallback(() => {
    onUseTextSearch?.();
    onClose();
  }, [onUseTextSearch, onClose]);

  if (!open) return null;

  const status = phase.status;
  const showViewport = status !== 'cameraError';
  const isScanning = status === 'scanning';
  const isResolving = status === 'resolving';
  const showReticle = isScanning || isResolving;
  // Dim the feed once we stop actively scanning (a match sheet or a message is up).
  const dimVideo = !isScanning && !isResolving;
  // The ONE live region conveying scan state to assistive tech — the reticle,
  // captions and toast are all aria-hidden decoration, so this never double-announces.
  const showMultiCodeHint = isScanning && multiCodeHint;
  const toastDescription =
    addedToast === null ? '' : `Qty ${addedToast.quantity} · ${addedToast.productName}`;
  const a11yStatus = addedToast
    ? `Added to cart, ${toastDescription}`
    : isResolving
      ? 'Barcode detected, loading product'
      : showMultiCodeHint
        ? 'More than one barcode in view, aim at a single barcode'
        : isScanning
          ? 'Scanning for a barcode'
          : '';

  return (
    <Modal open={open} onClose={onClose} title="Scan a barcode" size="fullscreen" hideChrome>
      <div className={styles.scan}>
        <header className={styles.topBar}>
          <span className={styles.cartChip}>
            <Icon name="cart" size={16} aria-hidden />
            <span>
              {itemCount} · {cartTotal}
            </span>
          </span>
          <button
            type="button"
            className={styles.close}
            aria-label="Close scanner"
            onClick={onClose}
          >
            <Icon name="x" size={22} />
          </button>
        </header>

        <div className={styles.stage}>
          {showViewport ? (
            <video
              ref={videoRef}
              className={styles.video}
              data-dim={dimVideo || undefined}
              muted
              playsInline
              aria-hidden="true"
            />
          ) : null}

          {showReticle ? (
            <div className={styles.reticleLayer} aria-hidden="true">
              <p className={styles.caption}>
                {isResolving ? SCAN_STATUS_COPY.detected.title : SCAN_STATUS_COPY.scanning}
              </p>
              <div className={styles.reticle} data-state={isResolving ? 'detected' : 'scanning'}>
                <span className={`${styles.corner} ${styles.cornerTL}`} />
                <span className={`${styles.corner} ${styles.cornerTR}`} />
                <span className={`${styles.corner} ${styles.cornerBL}`} />
                <span className={`${styles.corner} ${styles.cornerBR}`} />
                {isResolving ? (
                  <span className={styles.detectedCheck}>
                    <Icon name="check" size={28} />
                  </span>
                ) : (
                  <span className={styles.scanLine} />
                )}
              </div>
              {isResolving ? (
                <p className={styles.subCaption}>{SCAN_STATUS_COPY.detected.body}</p>
              ) : showMultiCodeHint ? (
                <p className={styles.subCaption}>Aim at a single barcode</p>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Overlays positioned against the full screen (not the inset .stage) so the
            bottom sheet is flush to the viewport edge and no shadow is clipped. */}
        <span role="status" className={styles.visuallyHidden}>
          {a11yStatus}
        </span>

        {addedToast ? (
          <div className={styles.toast} aria-hidden="true">
            <Toast
              key={addedToast.id}
              title="Added to cart"
              description={toastDescription}
              kind="addedToCart"
            />
          </div>
        ) : null}

        {isScanning ? (
          <div className={styles.doneBar}>
            <Button variant="secondary" onClick={onClose}>
              Done
            </Button>
          </div>
        ) : null}

        {/* Sheet / message surfaces sit ON TOP of the (dimmed) feed. */}
        <ScanResultPanel
          phase={phase}
          onSelectCandidate={selectCandidate}
          onRetry={retry}
          onUseTextSearch={useTextSearch}
          onAdded={handleAdded}
        />
      </div>
    </Modal>
  );
}

export default ScanModal;
